import { useState, useEffect } from 'react'
import { fmtDate, fmtDateTime } from '../../utils/format'
import { useToast } from '../../contexts/ToastContext'
import DateInput from '../../components/DateInput'
import {
  CheckCircleIcon,
  ClockIcon,
  TruckIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  EyeIcon,
  XMarkIcon,
  ShoppingCartIcon,
  DocumentTextIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'

interface Supplier {
  id: number
  trade_name: string
  nif: string
  is_self?: boolean
}

interface Unit {
  id: number
  name: string
}

interface Allergen {
  id: number
  name: string
  code: string
  svg_icon: string
}

interface Brand {
  id: number
  name: string
  ingredients_text?: string | null
  allergens?: Allergen[]
}

interface Ingredient {
  id: number
  name: string
  default_unit_id?: number
  allergens?: Allergen[]
  brands?: Brand[]
}

interface PurchaseOrderItem {
  id: number
  ingredient_id: number
  ingredient?: Ingredient
  quantity: number
  unit_id: number
  unit?: Unit
  unit_price?: number
}

interface PurchaseOrder {
  id: number
  code: string
  supplier_id?: number
  supplier?: Supplier
  status: 'draft' | 'sent' | 'partial' | 'received'
  items?: PurchaseOrderItem[]
  items_count?: number
  receipts_count?: number
  created_at: string
  sent_at?: string
}

interface ReceiptItem {
  ingredient_id: number
  ingredient?: Ingredient
  brand?: string
  brand_id?: number | null
  brand_ref?: { id: number; name: string } | null
  supplier_id: number
  lot_number: string
  best_before?: string
  expiry_date?: string
  purchase_date?: string
  quantity: number
  unit_id: number
  unit?: Unit
  unit_price?: number
  total_price?: number
  temperature_status?: 'ok' | 'warning' | 'rejected'
  temperature_value?: number | null
  extra_allergens?: number[]
  notes?: string
  _validated?: boolean // client-only: item already has inventory batch
}

interface Receipt {
  id: number
  code: string
  purchase_order_id?: number
  purchase_order?: PurchaseOrder
  supplier_id: number
  supplier?: Supplier
  received_at: string
  invoice_number?: string
  delivery_note?: string
  total_amount?: number
  notes?: string
  received_by?: number
  status: 'draft' | 'partial' | 'validated' | 'cancelled'
  items?: ReceiptItem[]
  items_count?: number
  created_at: string
}

const API_BASE_URL = '/api/v1/dev'

export default function Receiving() {
  const toast = useToast()
  const [receipts, setReceipts] = useState<Receipt[]>([])
  const [pendingOrders, setPendingOrders] = useState<PurchaseOrder[]>([])
  const [suppliers, setSuppliers] = useState<Supplier[]>([])
  const [ingredients, setIngredients] = useState<Ingredient[]>([])
  const [units, setUnits] = useState<Unit[]>([])
  const [allergens, setAllergens] = useState<Allergen[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [selectedTab, setSelectedTab] = useState<'pending' | 'received'>('pending')
  const [showReceiveModal, setShowReceiveModal] = useState(false)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [showAllergenModal, setShowAllergenModal] = useState(false)
  const [selectedItemIndex, setSelectedItemIndex] = useState<number | null>(null)
  const [selectedOrder, setSelectedOrder] = useState<PurchaseOrder | null>(null)
  const [selectedReceipt, setSelectedReceipt] = useState<Receipt | null>(null)

  // Form data
  const [formData, setFormData] = useState({
    receipt_id: null as number | null, // If set, we're editing an existing receipt
    purchase_order_id: null as number | null,
    supplier_id: 0,
    received_at: new Date().toISOString().split('T')[0], // Default to today
    invoice_number: '',
    delivery_note: '',
    total_amount: null as number | null,
    notes: '',
    items: [] as ReceiptItem[]
  })

  // Fetch data
  const fetchReceipts = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/receipts`)
      const data = await response.json()
      if (response.ok) {
        setReceipts(data.data || [])
      }
    } catch (err) {
      console.error('Error fetching receipts:', err)
    }
  }

  const fetchPendingOrders = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/purchase-orders?status=sent`)
      const data = await response.json()
      if (response.ok) {
        setPendingOrders(data.data || [])
      }

      // Also fetch partial orders
      const partialResponse = await fetch(`${API_BASE_URL}/purchase-orders?status=partial`)
      const partialData = await partialResponse.json()
      if (partialResponse.ok) {
        setPendingOrders(prev => [...prev, ...(partialData.data || [])])
      }
    } catch (err) {
      console.error('Error fetching pending orders:', err)
    }
  }

  const fetchSuppliers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/suppliers`)
      const data = await response.json()
      if (response.ok) {
        // Excluir proveedor propio - no podemos recibir mercancía de nosotros mismos
        const externalSuppliers = (data.data || []).filter((s: Supplier) => !s.is_self)
        setSuppliers(externalSuppliers)
      }
    } catch (err) {
      console.error('Error fetching suppliers:', err)
    }
  }

  const fetchIngredients = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/ingredients`)
      const data = await response.json()
      if (response.ok) {
        setIngredients(data.data || [])
      }
    } catch (err) {
      console.error('Error fetching ingredients:', err)
    }
  }

  const fetchUnits = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/units`)
      const data = await response.json()
      if (response.ok) {
        setUnits(data.data || [])
      }
    } catch (err) {
      console.error('Error fetching units:', err)
    }
  }

  const fetchAllergens = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/allergens`)
      const data = await response.json()
      if (response.ok) {
        setAllergens(data.data || [])
      }
    } catch (err) {
      console.error('Error fetching allergens:', err)
    }
  }

  useEffect(() => {
    const loadData = async () => {
      setLoading(true)
      await Promise.all([
        fetchReceipts(),
        fetchPendingOrders(),
        fetchSuppliers(),
        fetchIngredients(),
        fetchUnits(),
        fetchAllergens()
      ])
      setLoading(false)
    }
    loadData()
  }, [])

  // Get status badge
  const getStatusBadge = (status: string) => {
    const statusConfig = {
      draft: { color: 'bg-gray-100 text-gray-800', icon: ClockIcon, text: 'Borrador' },
      partial: { color: 'bg-yellow-100 text-yellow-800', icon: ExclamationTriangleIcon, text: 'Parcial' },
      validated: { color: 'bg-green-100 text-green-800', icon: CheckCircleIcon, text: 'Validado' },
      cancelled: { color: 'bg-red-100 text-red-800', icon: XMarkIcon, text: 'Cancelado' }
    }

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.draft
    const Icon = config.icon

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
        <Icon className="w-4 h-4 mr-1" />
        {config.text}
      </span>
    )
  }

  // Handle receive from purchase order
  const handleReceiveFromOrder = async (order: PurchaseOrder) => {
    try {
      // First, check if there's already a draft or partial receipt for this order
      const draftResponse = await fetch(`${API_BASE_URL}/receipts?purchase_order_id=${order.id}&status=draft`)
      const partialResponse = await fetch(`${API_BASE_URL}/receipts?purchase_order_id=${order.id}&status=partial`)
      const draftData = await draftResponse.json()
      const partialData = await partialResponse.json()

      const existingReceipt = (draftData.data?.[0]) || (partialData.data?.[0])

      if (existingReceipt) {
        // Load existing draft/partial receipt directly
        const receiptResponse = await fetch(`${API_BASE_URL}/receipts/${existingReceipt.id}`)
        const receiptData = await receiptResponse.json()

        if (receiptResponse.ok) {
          const receipt = receiptData.data
          // Load ALL items: validated ones shown as locked, pending ones editable
          const allItems = receipt.items.map((item: any) => ({
            ingredient_id: item.ingredient_id,
            brand: item.brand || '',
            brand_id: item.brand_id ?? null,
            supplier_id: item.supplier_id,
            lot_number: item.lot_number || '',
            best_before: item.best_before || '',
            expiry_date: item.expiry_date ? new Date(item.expiry_date).toISOString().split('T')[0] : '',
            purchase_date: item.purchase_date || '',
            quantity: item.quantity ? parseFloat(parseFloat(String(item.quantity)).toFixed(2)) : 0,
            unit_id: item.unit_id || 0,
            unit_price: item.unit_price ? parseFloat(parseFloat(String(item.unit_price)).toFixed(2)) : item.unit_price,
            temperature_status: item.temperature_status || 'ok',
            temperature_value: item.temperature_value,
            extra_allergens: item.extra_allergens || [],
            notes: item.notes || '',
            _validated: !!item.inventory_batch,
          }))
          // Sort: pending first, then validated
          allItems.sort((a: any, b: any) => (a._validated === b._validated ? 0 : a._validated ? 1 : -1))
          const statusLabel = existingReceipt.status === 'partial' ? 'parcial' : 'borrador'
          toast.info(`Cargada recepción ${statusLabel} existente (${existingReceipt.code})`)
          setSelectedOrder(order)
          setFormData({
            receipt_id: existingReceipt.id,
            purchase_order_id: receipt.purchase_order_id,
            supplier_id: receipt.supplier_id,
            received_at: receipt.received_at ? new Date(receipt.received_at).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
            invoice_number: receipt.invoice_number || '',
            delivery_note: receipt.delivery_note || '',
            total_amount: receipt.total_amount,
            notes: receipt.notes || '',
            items: allItems
          })
          setShowReceiveModal(true)
          return
        }
      }

      // No draft found or user chose to create new, load from order
      const response = await fetch(`${API_BASE_URL}/receipts/from-order/${order.id}`)
      const data = await response.json()

      if (response.ok) {
        setSelectedOrder(order)
        setFormData({
          receipt_id: null,
          purchase_order_id: order.id,
          supplier_id: order.supplier_id || 0,
          received_at: new Date().toISOString().split('T')[0],
          invoice_number: '',
          delivery_note: '',
          total_amount: null,
          notes: '',
          items: data.data.suggested_items.map((item: any) => ({
            ingredient_id: item.ingredient_id,
            brand: '',
            supplier_id: item.supplier_id,
            lot_number: '',
            best_before: '',
            expiry_date: '',
            purchase_date: item.purchase_date,
            quantity: parseFloat(parseFloat(String(item.quantity)).toFixed(2)),
            unit_id: item.unit_id,
            unit_price: item.unit_price ? parseFloat(parseFloat(String(item.unit_price)).toFixed(2)) : item.unit_price,
            temperature_status: 'ok',
            temperature_value: null,
            extra_allergens: [],
            notes: ''
          }))
        })
        setShowReceiveModal(true)
      } else {
        toast.error(data.message || 'Error al cargar datos de la orden')
      }
    } catch (err) {
      toast.error('Error de conexión con el servidor')
      console.error('Error loading order data:', err)
    }
  }

  // Handle receive without order
  const handleReceiveWithoutOrder = () => {
    setSelectedOrder(null)
    setFormData({
      receipt_id: null,
      purchase_order_id: null,
      supplier_id: 0,
      received_at: new Date().toISOString().split('T')[0],
      invoice_number: '',
      delivery_note: '',
      total_amount: null,
      notes: '',
      items: []
    })
    setShowReceiveModal(true)
  }

  // Add item
  const addItem = () => {
    setFormData({
      ...formData,
      items: [
        {
          ingredient_id: 0,
          brand: '',
          supplier_id: formData.supplier_id,
          lot_number: '',
          best_before: '',
          expiry_date: '',
          purchase_date: new Date().toISOString().split('T')[0],
          quantity: 1,
          unit_id: 0,
          unit_price: 0,
          temperature_status: 'ok',
          temperature_value: null,
          extra_allergens: [],
          notes: ''
        },
        ...formData.items
      ]
    })
  }

  // Remove item
  const removeItem = (index: number) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index)
    })
  }

  // Update item
  const updateItem = (index: number, field: keyof ReceiptItem, value: any) => {
    const updatedItems = [...formData.items]
    // If changing ingredient, auto-load its default unit
    if (field === 'ingredient_id') {
      const selectedIngredient = ingredients.find(ing => ing.id === value)
      // Preseleccionar marca si el ingrediente tiene exactamente una; si no, limpiar
      const brands = selectedIngredient?.brands || []
      const autoBrandId = brands.length === 1 ? brands[0].id : null
      updatedItems[index] = {
        ...updatedItems[index],
        ingredient_id: value,
        brand_id: autoBrandId,
        brand: '',
        ...(selectedIngredient?.default_unit_id ? { unit_id: selectedIngredient.default_unit_id } : {}),
      }
    } else {
      updatedItems[index] = {
        ...updatedItems[index],
        [field]: value
      }
    }
    setFormData({
      ...formData,
      items: updatedItems
    })
  }

  // Open allergen modal for item
  const openAllergenModal = (index: number) => {
    setSelectedItemIndex(index)
    setShowAllergenModal(true)
  }

  // Get inherited allergens for an ingredient
  const getInheritedAllergens = (ingredientId: number): number[] => {
    const ingredient = ingredients.find(ing => ing.id === ingredientId)
    return ingredient?.allergens?.map(a => a.id) || []
  }

  // Toggle extra allergen
  const toggleExtraAllergen = (allergenId: number) => {
    if (selectedItemIndex === null) return

    const item = formData.items[selectedItemIndex]
    const extraAllergens = item.extra_allergens || []

    if (extraAllergens.includes(allergenId)) {
      // Remove allergen
      updateItem(selectedItemIndex, 'extra_allergens', extraAllergens.filter(id => id !== allergenId))
    } else {
      // Add allergen
      updateItem(selectedItemIndex, 'extra_allergens', [...extraAllergens, allergenId])
    }
  }

  // Save receipt
  const handleSaveReceipt = async () => {
    // Basic validation only - strict validation happens at validate time
    if (!formData.supplier_id) {
      toast.error('Debe seleccionar un proveedor')
      return
    }

    if (formData.items.length === 0 && !formData.receipt_id) {
      toast.error('Debe agregar al menos un item')
      return
    }

    // Only check pending (non-validated) items
    const pendingItems = formData.items.filter(item => !item._validated)
    for (let i = 0; i < pendingItems.length; i++) {
      if (!pendingItems[i].ingredient_id) {
        toast.error(`Item pendiente ${i + 1}: Debe seleccionar un ingrediente`)
        return
      }
    }

    setSaving(true)

    try {
      const isUpdating = formData.receipt_id !== null
      const url = isUpdating
        ? `${API_BASE_URL}/receipts/${formData.receipt_id}`
        : `${API_BASE_URL}/receipts`

      const method = isUpdating ? 'PUT' : 'POST'

      // Remove receipt_id and _validated items from the payload
      const { receipt_id, ...payloadData } = formData
      const payloadToSend = {
        ...payloadData,
        items: payloadData.items
          .filter(item => !item._validated)
          .map(({ _validated, ...rest }) => rest)
      }

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payloadToSend),
      })

      const data = await response.json()

      if (response.ok) {
        await fetchReceipts()
        await fetchPendingOrders()
        toast.success(formData.receipt_id ? 'Recepción actualizada' : 'Recepción guardada')
        setShowReceiveModal(false)
        setFormData({
          receipt_id: null,
          purchase_order_id: null,
          supplier_id: 0,
          received_at: new Date().toISOString().split('T')[0],
          invoice_number: '',
          delivery_note: '',
          total_amount: null,
          notes: '',
          items: []
        })
        setSelectedOrder(null)
      } else {
        toast.error(data.message || 'Error al guardar la recepción')
      }
    } catch (err) {
      toast.error('Error de conexión con el servidor')
      console.error('Error saving receipt:', err)
    } finally {
      setSaving(false)
    }
  }

  // View receipt details
  const viewReceiptDetails = async (receiptId: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/receipts/${receiptId}`)
      const data = await response.json()

      if (response.ok) {
        setSelectedReceipt(data.data)
        setShowDetailModal(true)
      } else {
        toast.error('Error al cargar detalles de la recepción')
      }
    } catch (err) {
      toast.error('Error de conexión con el servidor')
      console.error('Error fetching receipt details:', err)
    }
  }

  // Validate receipt
  const validateReceipt = async (receiptId: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/receipts/${receiptId}/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      const data = await response.json()

      if (response.ok) {
        await fetchReceipts()
        await fetchPendingOrders()

        const result = data.validation_result
        if (result) {
          if (result.validated_count > 0) {
            toast.success(`${result.validated_count} item(s) validado(s) — lotes creados`)
          }
          if (result.pending_count > 0) {
            toast.error(`${result.pending_count} item(s) pendiente(s) — datos incompletos`)
          }
          if (result.skipped_count > 0) {
            toast.info(`${result.skipped_count} item(s) rechazado(s)`)
          }
          if (result.errors?.length > 0) {
            result.errors.forEach((e: any) => toast.error(`${e.ingredient}: ${e.error}`))
          }
        } else {
          toast.success('Recepción procesada')
        }
      } else {
        toast.error(data.message || 'Error al validar la recepción')
      }
    } catch (err) {
      toast.error('Error de conexión con el servidor')
      console.error('Error validating receipt:', err)
    }
  }

  // Cancel receipt
  const cancelReceipt = async (receiptId: number) => {
    if (!confirm('¿Está seguro de cancelar esta recepción?')) {
      return
    }

    try {
      const response = await fetch(`${API_BASE_URL}/receipts/${receiptId}/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (response.ok) {
        await fetchReceipts()
        toast.success('Recepción cancelada')
      } else {
        const data = await response.json()
        toast.error(data.message || 'Error al cancelar la recepción')
      }
    } catch (err) {
      toast.error('Error de conexión con el servidor')
      console.error('Error cancelling receipt:', err)
    }
  }

  const filteredReceipts = receipts.filter(r => r.status !== 'cancelled')

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Recepción de Mercancías</h1>
          <p className="mt-1 text-sm text-gray-500">
            Gestiona la recepción de productos y crea lotes de inventario
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          <button
            onClick={handleReceiveWithoutOrder}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            Recepción Libre
          </button>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClockIcon className="h-6 w-6 text-yellow-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Órdenes Pendientes
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {pendingOrders.length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TruckIcon className="h-6 w-6 text-blue-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Recibidos Hoy
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {receipts.filter(r =>
                      new Date(r.received_at).toDateString() === new Date().toDateString()
                    ).length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <DocumentTextIcon className="h-6 w-6 text-orange-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Borradores
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {receipts.filter(r => r.status === 'draft').length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-6 w-6 text-yellow-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Parciales
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {receipts.filter(r => r.status === 'partial').length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-6 w-6 text-green-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Validados
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {receipts.filter(r => r.status === 'validated').length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white shadow rounded-lg">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => setSelectedTab('pending')}
              className={`flex-1 py-4 px-1 text-center border-b-2 font-medium text-sm ${
                selectedTab === 'pending'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Órdenes Pendientes ({pendingOrders.length})
            </button>
            <button
              onClick={() => setSelectedTab('received')}
              className={`flex-1 py-4 px-1 text-center border-b-2 font-medium text-sm ${
                selectedTab === 'received'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Recepciones ({filteredReceipts.length})
            </button>
          </nav>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
            </div>
          ) : selectedTab === 'pending' ? (
            pendingOrders.length === 0 ? (
              <div className="text-center py-12">
                <ShoppingCartIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No hay órdenes pendientes</h3>
                <p className="mt-1 text-sm text-gray-500">Todas las órdenes han sido recibidas.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingOrders.map((order) => (
                  <div key={order.id} className="border rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center space-x-2">
                          <h3 className="text-lg font-medium text-gray-900">{order.code}</h3>
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            order.status === 'sent' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {order.status === 'sent' ? 'Enviada' : 'Parcial'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          {order.supplier?.trade_name || 'Sin proveedor'} • {order.items_count || 0} items
                        </p>
                        {order.sent_at && (
                          <p className="text-xs text-gray-500 mt-1">
                            Enviada: {fmtDate(order.sent_at)}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => handleReceiveFromOrder(order)}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        <TruckIcon className="h-5 w-5 mr-2" />
                        Recibir Mercancía
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            filteredReceipts.length === 0 ? (
              <div className="text-center py-12">
                <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No hay recepciones</h3>
                <p className="mt-1 text-sm text-gray-500">Comienza recibiendo mercancía de una orden.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Código
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Proveedor
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Orden de Compra
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Items
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Estado
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Fecha Recepción
                      </th>
                      <th className="relative px-6 py-3">
                        <span className="sr-only">Acciones</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredReceipts.map((receipt) => (
                      <tr key={receipt.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {receipt.code}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {receipt.supplier?.trade_name || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {receipt.purchase_order?.code || 'Recepción libre'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {receipt.items_count || 0} items
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(receipt.status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {fmtDateTime(receipt.received_at)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => viewReceiptDetails(receipt.id)}
                              className="text-primary-600 hover:text-primary-900"
                              title="Ver detalles"
                            >
                              <EyeIcon className="h-5 w-5" />
                            </button>
                            {(receipt.status === 'draft' || receipt.status === 'partial') && (
                              <button
                                onClick={() => validateReceipt(receipt.id)}
                                className="text-green-600 hover:text-green-900"
                                title="Validar recepción"
                              >
                                <CheckCircleIcon className="h-5 w-5" />
                              </button>
                            )}
                            {receipt.status === 'draft' && (
                              <button
                                onClick={() => cancelReceipt(receipt.id)}
                                className="text-red-600 hover:text-red-900"
                                title="Cancelar"
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </div>
      </div>

      {/* Receive Modal */}
      {showReceiveModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
            <div className="bg-white rounded-lg overflow-hidden shadow-xl transform transition-all max-w-6xl w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    {formData.receipt_id ? (
                      <>Editar Recepción{selectedOrder && ` - ${selectedOrder.code}`}</>
                    ) : (
                      selectedOrder ? `Recibir Mercancía - ${selectedOrder.code}` : 'Recepción Libre'
                    )}
                  </h3>
                  <button
                    onClick={() => setShowReceiveModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-4 max-h-[70vh] overflow-y-auto">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Proveedor *
                      </label>
                      <select
                        value={formData.supplier_id}
                        onChange={(e) => setFormData({ ...formData, supplier_id: parseInt(e.target.value) })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                        disabled={!!selectedOrder}
                        required
                      >
                        <option value={0}>Seleccionar proveedor</option>
                        {suppliers.map((supplier) => (
                          <option key={supplier.id} value={supplier.id}>
                            {supplier.trade_name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Fecha de Recepción *
                      </label>
                      <DateInput
                        value={formData.received_at}
                        onChange={(v) => setFormData({ ...formData, received_at: v })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                        required
                        min={selectedOrder ? selectedOrder.created_at.split('T')[0] : undefined}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Nº Factura
                      </label>
                      <input
                        type="text"
                        value={formData.invoice_number}
                        onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm placeholder:italic placeholder:text-gray-300"
                        placeholder="FRA-2024-001"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Nº Albarán
                      </label>
                      <input
                        type="text"
                        value={formData.delivery_note}
                        onChange={(e) => setFormData({ ...formData, delivery_note: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm placeholder:italic placeholder:text-gray-300"
                        placeholder="ALB-2024-001"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Importe Total
                      </label>
                      <input
                        type="number"
                        value={formData.total_amount || ''}
                        onChange={(e) => setFormData({ ...formData, total_amount: parseFloat(e.target.value) || null })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm placeholder:italic placeholder:text-gray-300"
                        placeholder="0.00"
                        min="0"
                        step="0.5"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Notas
                    </label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      rows={2}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm placeholder:italic placeholder:text-gray-300"
                      placeholder="Observaciones sobre la recepción..."
                    />
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Items de la recepción *
                      </label>
                      <button
                        type="button"
                        onClick={addItem}
                        className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                      >
                        <PlusIcon className="h-4 w-4 mr-1" />
                        Agregar Item
                      </button>
                    </div>

                    {formData.items.length === 0 ? (
                      <div className="text-center py-8 bg-gray-50 rounded-md">
                        <p className="text-sm text-gray-500">
                          No hay items agregados. Haz clic en "Agregar Item" para comenzar.
                        </p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto border rounded-md">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                Ingrediente *
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                Marca
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                Lote
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                Cantidad
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                Unidad
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                Caducidad
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                Temp.
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                Alérgenos
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">

                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {formData.items.map((item, index) => (
                                <tr key={index} className={item._validated ? 'bg-green-50 opacity-60' : ''}>
                                  <td className="px-3 py-2">
                                    {item._validated ? (
                                      <div className="flex items-center gap-1">
                                        <span className="text-xs text-green-700 font-medium">{ingredients.find(i => i.id === item.ingredient_id)?.name || 'N/A'}</span>
                                        <span className="inline-flex px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-green-100 text-green-800">Validado</span>
                                      </div>
                                    ) : (
                                      <select
                                        value={item.ingredient_id}
                                        onChange={(e) => updateItem(index, 'ingredient_id', parseInt(e.target.value))}
                                        className="w-full text-xs border-gray-300 rounded-md disabled:bg-gray-100 disabled:cursor-not-allowed"
                                        required
                                        disabled={item.ingredient_id > 0}
                                      >
                                        <option value={0}>Seleccionar</option>
                                        {ingredients.map((ing) => (
                                          <option key={ing.id} value={ing.id}>
                                            {ing.name}
                                          </option>
                                        ))}
                                      </select>
                                    )}
                                  </td>
                                  <td className="px-3 py-2">
                                    {(() => {
                                      const ing = ingredients.find(i => i.id === item.ingredient_id)
                                      const brandList = ing?.brands || []
                                      if (brandList.length === 0) {
                                        // Ingrediente sin marcas: texto libre opcional (compatibilidad)
                                        return (
                                          <input
                                            type="text"
                                            value={item.brand || ''}
                                            onChange={(e) => updateItem(index, 'brand', e.target.value)}
                                            className="w-24 text-xs border-gray-300 rounded-md disabled:bg-gray-100 disabled:cursor-not-allowed placeholder:italic placeholder:text-gray-300"
                                            placeholder="Marca"
                                            disabled={!!item._validated}
                                          />
                                        )
                                      }
                                      return (
                                        <select
                                          value={item.brand_id ?? ''}
                                          onChange={(e) => updateItem(index, 'brand_id', e.target.value ? Number(e.target.value) : null)}
                                          className="w-28 text-xs border-gray-300 rounded-md disabled:bg-gray-100 disabled:cursor-not-allowed"
                                          disabled={!!item._validated}
                                        >
                                          <option value="">— marca —</option>
                                          {brandList.map(b => (
                                            <option key={b.id} value={b.id}>{b.name}</option>
                                          ))}
                                        </select>
                                      )
                                    })()}
                                  </td>
                                  <td className="px-3 py-2">
                                    <input
                                      type="text"
                                      value={item.lot_number}
                                      onChange={(e) => updateItem(index, 'lot_number', e.target.value)}
                                      className="w-28 text-xs border-gray-300 rounded-md disabled:bg-gray-100 disabled:cursor-not-allowed placeholder:italic placeholder:text-gray-300"
                                      placeholder="LOT-001"
                                      disabled={!!item._validated}
                                    />
                                  </td>
                                  <td className="px-3 py-2">
                                    <input
                                      type="number"
                                      value={item.quantity}
                                      onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value))}
                                      className="w-20 text-xs border-gray-300 rounded-md disabled:bg-gray-100 disabled:cursor-not-allowed"
                                      min="0.5"
                                      step="0.5"
                                      disabled={!!item._validated}
                                    />
                                  </td>
                                  <td className="px-3 py-2">
                                    <select
                                      value={item.unit_id}
                                      onChange={(e) => updateItem(index, 'unit_id', parseInt(e.target.value))}
                                      className="w-24 text-xs border-gray-300 rounded-md disabled:bg-gray-100 disabled:cursor-not-allowed"
                                      required
                                      disabled={!!item._validated || item.ingredient_id > 0}
                                    >
                                      <option value={0}>Unidad</option>
                                      {units.map((unit) => (
                                        <option key={unit.id} value={unit.id}>
                                          {unit.name}
                                        </option>
                                      ))}
                                    </select>
                                  </td>
                                  <td className="px-3 py-2">
                                    {item._validated ? (
                                      <span className="text-xs text-gray-500">{item.expiry_date || '-'}</span>
                                    ) : (
                                      <DateInput
                                        value={item.expiry_date || ''}
                                        onChange={(v) => updateItem(index, 'expiry_date', v)}
                                        className="w-32 text-xs border-gray-300 rounded-md"
                                        min={formData.received_at}
                                      />
                                    )}
                                  </td>
                                  <td className="px-3 py-2">
                                    <select
                                      value={item.temperature_status || ''}
                                      onChange={(e) => updateItem(index, 'temperature_status', e.target.value || null)}
                                      className="w-24 text-xs border-gray-300 rounded-md disabled:bg-gray-100 disabled:cursor-not-allowed"
                                      disabled={!!item._validated}
                                    >
                                      <option value="">-</option>
                                      <option value="ok">Bien</option>
                                      <option value="warning">Advertencia</option>
                                      <option value="rejected">Rechazo</option>
                                    </select>
                                  </td>
                                  <td className="px-3 py-2">
                                    <button
                                      type="button"
                                      onClick={() => openAllergenModal(index)}
                                      disabled={!item.ingredient_id || !!item._validated}
                                      className="px-2 py-1 text-xs bg-primary-100 text-primary-700 rounded hover:bg-primary-200 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center space-x-1"
                                      title="Gestionar alérgenos"
                                    >
                                      <span>
                                        {(() => {
                                          const inherited = getInheritedAllergens(item.ingredient_id)
                                          const extra = item.extra_allergens || []
                                          const total = new Set([...inherited, ...extra]).size
                                          return total > 0 ? `${total}` : '0'
                                        })()}
                                      </span>
                                      <span className="text-lg">🏷️</span>
                                    </button>
                                  </td>
                                  <td className="px-3 py-2">
                                    {!item._validated && (
                                      <button
                                        type="button"
                                        onClick={() => removeItem(index)}
                                        className="text-red-600 hover:text-red-900"
                                      >
                                        <TrashIcon className="h-4 w-4" />
                                      </button>
                                    )}
                                  </td>
                                </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={handleSaveReceipt}
                  disabled={saving || (formData.items.length === 0 && !formData.receipt_id)}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {saving ? 'Guardando...' : formData.receipt_id ? 'Actualizar Recepción' : 'Guardar Recepción'}
                </button>
                <button
                  onClick={() => setShowReceiveModal(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedReceipt && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
            <div className="bg-white rounded-lg overflow-hidden shadow-xl transform transition-all max-w-6xl w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">
                      Detalles de Recepción - {selectedReceipt.code}
                    </h3>
                    {selectedReceipt.purchase_order && (
                      <p className="text-sm text-gray-500">
                        Orden de compra: {selectedReceipt.purchase_order.code}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Proveedor</p>
                      <p className="mt-1 text-sm text-gray-900">
                        {selectedReceipt.supplier?.trade_name || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Estado</p>
                      <div className="mt-1">
                        {getStatusBadge(selectedReceipt.status)}
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Fecha Recepción</p>
                      <p className="mt-1 text-sm text-gray-900">
                        {fmtDateTime(selectedReceipt.received_at)}
                      </p>
                    </div>
                    {selectedReceipt.invoice_number && (
                      <div>
                        <p className="text-sm font-medium text-gray-500">Nº Factura</p>
                        <p className="mt-1 text-sm text-gray-900">{selectedReceipt.invoice_number}</p>
                      </div>
                    )}
                  </div>

                  {selectedReceipt.notes && (
                    <div>
                      <p className="text-sm font-medium text-gray-500">Notas</p>
                      <p className="mt-1 text-sm text-gray-900">{selectedReceipt.notes}</p>
                    </div>
                  )}

                  {selectedReceipt.items && selectedReceipt.items.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-2">Items recibidos</p>
                      <div className="border rounded-md overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                Estado
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                Ingrediente
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                Marca
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                Lote
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                Cantidad
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                Caducidad
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                Temperatura
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                Alérgenos
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {selectedReceipt.items.map((item, index) => (
                              <tr key={index} className={(item as any).inventory_batch ? 'bg-green-50' : ''}>
                                <td className="px-4 py-2 text-sm">
                                  {(item as any).inventory_batch ? (
                                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                      Validado
                                    </span>
                                  ) : item.temperature_status === 'rejected' ? (
                                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                                      Rechazado
                                    </span>
                                  ) : (
                                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                                      Pendiente
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-900">
                                  {item.ingredient?.name || 'N/A'}
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-900">
                                  {item.brand_ref?.name || item.brand || '-'}
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-900">
                                  {item.lot_number}
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-900">
                                  {parseFloat(String(item.quantity)).toFixed(2)} {item.unit?.name || ''}
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-900">
                                  {fmtDate(item.expiry_date)}
                                </td>
                                <td className="px-4 py-2 text-sm">
                                  {item.temperature_status ? (
                                    <span className={`px-2 py-1 text-xs rounded-full ${
                                      item.temperature_status === 'ok' ? 'bg-green-100 text-green-800' :
                                      item.temperature_status === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                                      'bg-red-100 text-red-800'
                                    }`}>
                                      {item.temperature_status === 'ok' ? 'Bien' :
                                       item.temperature_status === 'warning' ? 'Advertencia' : 'Rechazo'}
                                    </span>
                                  ) : '-'}
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-900">
                                  <div className="flex flex-wrap gap-1">
                                    {(() => {
                                      const inheritedIds = item.ingredient?.allergens?.map(a => a.id) || []
                                      const extraIds = item.extra_allergens || []
                                      const allAllergenIds = [...new Set([...inheritedIds, ...extraIds])]

                                      if (allAllergenIds.length === 0) {
                                        return <span className="text-gray-400">Sin alérgenos</span>
                                      }

                                      return allAllergenIds.map(allergenId => {
                                        const allergen = allergens.find(a => a.id === allergenId)
                                        if (!allergen) return null

                                        const isInherited = inheritedIds.includes(allergenId)
                                        const isExtra = extraIds.includes(allergenId)

                                        return (
                                          <span
                                            key={allergenId}
                                            className={`inline-flex items-center px-2 py-1 text-xs rounded-full ${
                                              isInherited && !isExtra
                                                ? 'bg-blue-100 text-blue-800'
                                                : isExtra && !isInherited
                                                ? 'bg-green-100 text-green-800'
                                                : 'bg-purple-100 text-purple-800'
                                            }`}
                                            title={
                                              isInherited && !isExtra
                                                ? 'Heredado del ingrediente'
                                                : isExtra && !isInherited
                                                ? 'Agregado para esta marca'
                                                : 'Heredado + Agregado'
                                            }
                                          >
                                            <span className="mr-1">{allergen.svg_icon?.startsWith('<svg') ? <span dangerouslySetInnerHTML={{ __html: allergen.svg_icon }} /> : allergen.svg_icon}</span>
                                            {allergen.name}
                                          </span>
                                        )
                                      })
                                    })()}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:w-auto sm:text-sm"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Allergen Management Modal */}
      {showAllergenModal && selectedItemIndex !== null && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
            <div className="bg-white rounded-lg overflow-hidden shadow-xl transform transition-all max-w-2xl w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">
                      Gestión de Alérgenos
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {(() => {
                        const item = formData.items[selectedItemIndex]
                        const ingredient = ingredients.find(ing => ing.id === item.ingredient_id)
                        return ingredient?.name || 'Ingrediente no seleccionado'
                      })()}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowAllergenModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                    <p className="text-sm text-blue-700">
                      <strong>Nota:</strong> Los alérgenos marcados en azul son heredados del ingrediente y no se pueden desmarcar.
                      Puedes agregar alérgenos adicionales específicos de la marca recibida.
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-3 max-h-96 overflow-y-auto">
                    {allergens.map((allergen) => {
                      const item = formData.items[selectedItemIndex]
                      const inheritedAllergens = getInheritedAllergens(item.ingredient_id)
                      const extraAllergens = item.extra_allergens || []
                      const isInherited = inheritedAllergens.includes(allergen.id)
                      const isExtra = extraAllergens.includes(allergen.id)
                      const isChecked = isInherited || isExtra

                      return (
                        <label
                          key={allergen.id}
                          className={`flex items-center space-x-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                            isInherited
                              ? 'bg-blue-50 border-blue-300 cursor-not-allowed'
                              : isExtra
                              ? 'bg-green-50 border-green-300 hover:bg-green-100'
                              : 'bg-white border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            disabled={isInherited}
                            onChange={() => toggleExtraAllergen(allergen.id)}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded disabled:cursor-not-allowed"
                          />
                          <span className="text-2xl">{allergen.svg_icon?.startsWith('<svg') ? <span dangerouslySetInnerHTML={{ __html: allergen.svg_icon }} /> : allergen.svg_icon}</span>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">
                              {allergen.name}
                            </p>
                            {isInherited && (
                              <p className="text-xs text-blue-600">Heredado del ingrediente</p>
                            )}
                            {isExtra && !isInherited && (
                              <p className="text-xs text-green-600">Agregado para esta marca</p>
                            )}
                          </div>
                        </label>
                      )
                    })}
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={() => setShowAllergenModal(false)}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:w-auto sm:text-sm"
                >
                  Guardar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
